import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, describeApiError } from '../lib/api';
import { NoteActionsReview } from './NoteActionsReview';
import type { NoteProposal } from '../../../shared/noteActions';
import { formatDateAU, formatRelativeDays } from '../../../shared/format';
import { QAEmpty } from './QAEmpty';
import type { FileNoteRecord } from '../../../shared/api';

const TYPE_LABEL: Record<string, string> = {
  call: 'Call', email: 'Email', text: 'Text', meeting: 'Meeting',
  advice_sent: 'Advice sent', approval_received: 'Approval received', other: 'Other',
};

export function FileNotesTab({ clientId, initial }: { clientId: string; initial: FileNoteRecord[] }) {
  const qc = useQueryClient();
  const [type, setType] = useState<string>('meeting');
  const [content, setContent] = useState('');
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [proposal, setProposal] = useState<NoteProposal | null>(null);
  const [reader, setReader] = useState<{ reader: 'ai' | 'rules'; fallbackReason: string | null }>({ reader: 'rules', fallbackReason: null });
  const [notice, setNotice] = useState<string | null>(null);
  const [finding, setFinding] = useState(false);

  async function findActions() {
    setFinding(true); setNotice(null);
    try {
      const r = await api<{ data: NoteProposal; reader: 'ai' | 'rules'; fallbackReason: string | null }>(`/api/clients/${clientId}/note-actions/propose`, { method: 'POST', body: JSON.stringify({ note: content }) });
      setReader({ reader: r.reader ?? 'rules', fallbackReason: r.fallbackReason ?? null });
      setProposal(r.data);
    } catch (e) { setNotice(describeApiError(e)); } finally { setFinding(false); }
  }

  const { data } = useQuery({
    queryKey: ['client', clientId, 'file-notes'],
    queryFn: () => api<{ data: FileNoteRecord[] }>(`/api/clients/${clientId}/file-notes`),
    initialData: { data: initial },
  });

  const create = useMutation({
    mutationFn: (body: { type: string; content: string }) =>
      api(`/api/clients/${clientId}/file-notes`, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      setContent('');
      qc.invalidateQueries({ queryKey: ['client', clientId, 'file-notes'] });
      qc.invalidateQueries({ queryKey: ['client', clientId, 'full'] });
    },
  });

  const filtered = useMemo(() => {
    const rows = data?.data ?? [];
    return rows.filter((n) =>
      (!search || n.content.toLowerCase().includes(search.toLowerCase())) &&
      (!filterType || n.type === filterType),
    );
  }, [data, search, filterType]);

  return (
    <div className="mt-6">
      <section className="qa-card p-6 mb-6">
        <p className="qa-eyebrow mb-3">Add a file note</p>
        <div className="flex gap-3 mb-3">
          <select className="qa-select sm:w-48" value={type} onChange={e => setType(e.target.value)}>
            {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <textarea className="qa-textarea min-h-[100px]" placeholder="What was discussed, decided, or actioned?" value={content} onChange={e => setContent(e.target.value)} />
        {!proposal && (
          <div className="flex justify-end gap-2 mt-3">
            <button type="button" className="btn-ghost text-sm" disabled={!content.trim() || finding} onClick={findActions}
              title="Read the note for tasks, compliance dates, the next review and RoA scope">
              {finding ? 'Reading...' : 'Find actions'}
            </button>
            <button className="btn-primary" disabled={!content.trim() || create.isPending} onClick={() => create.mutate({ type, content })}>
              {create.isPending ? 'Saving…' : 'Add note'}
            </button>
          </div>
        )}
        {proposal && (
          <NoteActionsReview clientId={clientId} note={content} noteType={type} proposal={proposal} reader={reader.reader} fallbackReason={reader.fallbackReason}
            onCancel={() => setProposal(null)}
            onDone={(msg) => {
              setProposal(null); setContent(''); setNotice(msg);
              void qc.invalidateQueries({ queryKey: ['client', clientId] });
            }} />
        )}
        {notice && <p role="status" className="text-xs text-ink-soft mt-2">{notice}</p>}
      </section>

      <div className="flex gap-3 mb-4 flex-col sm:flex-row">
        <input type="search" className="qa-input flex-1" placeholder="Search notes" value={search} onChange={e => setSearch(e.target.value)} />
        <select className="qa-select sm:w-48" value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">All types</option>
          {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      {filtered.length === 0 ? (
        <QAEmpty inline title="No notes match this filter." />
      ) : (
        <ol className="space-y-3">
          {filtered.map((n) => (
            <li key={n.id} className="qa-card p-5">
              <div className="flex justify-between items-baseline mb-2">
                <span className="qa-eyebrow">{TYPE_LABEL[n.type] ?? n.type}</span>
                <span className="qa-mono text-xs text-ink-mute">{formatDateAU(n.occurredAt)} · {formatRelativeDays(n.occurredAt)}</span>
              </div>
              <p className="text-sm whitespace-pre-wrap">{n.content}</p>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
